// Story 10.1 — Login page. Sin AppShell. Auth via cookie de sesión
// (Story 5.2) — `POST /api/v1/auth/login` setea la cookie automáticamente.

import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, api } from '@/lib/api-client';

export function LoginPage(): React.ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const qc = useQueryClient();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/api/v1/auth/login', { email, password });
      // P-219: limpiamos la contraseña del state ANTES de navegar para
      // que no quede en memoria de React DevTools / heap snapshot. El
      // input es uncontrolled tras el unmount de todas formas, pero un
      // re-render intermedio podría exponerla.
      setPassword('');
      setEmail('');
      // La sesión es nueva: nada de la anterior debe quedar en caché (como en
      // InvitePage y ResetPasswordPage). Invalidar no alcanzaba: si se entró
      // por una página con sesión, `auth/me` quedaba cacheado como `null` y
      // el guard del router (`ensureQueryData`) lo devolvía sin refetch, así
      // que el login exitoso volvía al login.
      qc.clear();
      // P-217: validar el redirect — solo paths internos que empiezan
      // con `/` y NO con `//` (protocol-relative) ni `/\` (Windows-ish
      // bypass). Bloquea `javascript:` y absolutas. Sin esto, un link
      // tipo `/login?redirect=//evil.com` después del submit hace
      // open redirect a un dominio externo.
      const safeRedirect =
        search.redirect &&
        search.redirect !== '/login' &&
        /^\/(?![/\\])[^\s]*$/.test(search.redirect)
          ? search.redirect
          : '/';
      // biome-ignore lint/suspicious/noExplicitAny: TanStack typed-routes requires runtime narrowing the test prefers to skip.
      await navigate({ to: safeRedirect as any });
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 401
          ? 'Email o contraseña incorrectos.'
          : 'No se pudo iniciar sesión. Inténtalo de nuevo.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Iniciar sesión</CardTitle>
          <CardDescription>Entra con tu cuenta de Revelation Bots.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </CardContent>
          <CardFooter className="flex-col gap-3">
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Ingresando…' : 'Ingresar'}
            </Button>
            {/* Story 16.6 — sin SMTP, el reset lo genera un admin. */}
            <p className="text-center text-xs text-muted-foreground">
              ¿Olvidaste tu contraseña? Pídele a tu administrador un link para cambiarla.
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
